import { useState } from "react";
import { X, Plus } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { useDeviceStore } from "../../../store/useDeviceStore";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useCreateParameter, useUpdateParameter } from "../../../queries/parameters";
import { ParameterCreateDto, ParameterUpdateDto } from "../../../types/device";
import { toast } from "react-toastify";
import { IconSelector } from "./IconSelector";

export interface ParameterFormData {
  id?: string; // Add optional id for edit mode
  deviceId: string;
  name: string;
  identifier: string;
  unit: string;
  dataType: "INTEGER" | "FLOAT" | "STRING" | "BOOLEAN";
  icon?: string;
  calibration?: number;
  minValue?: number;
  maxValue?: number;
}

interface AddParameterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (parameter: ParameterFormData) => void;
  deviceId?: string;
  editParameter?: ParameterFormData;
  isEditMode?: boolean; // Add this prop to explicitly control edit mode
}

// Validation schema using Yup
const validationSchema = Yup.object().shape({
  deviceId: Yup.string().optional(),
  name: Yup.string().required("Name is required"),
  identifier: Yup.string()
    .required("Identifier is required")
    .matches(
      /^[a-zA-Z0-9_]+$/,
      "Identifier must not contain special characters"
    ),
  // STRING parameters have no unit; require it only for the other types.
  unit: Yup.string().when("dataType", {
    is: "STRING",
    then: (schema) => schema.optional(),
    otherwise: (schema) => schema.required("Unit is required"),
  }),
  dataType: Yup.string()
    .oneOf(["INTEGER", "FLOAT", "STRING", "BOOLEAN"])
    .required(),
  icon: Yup.string().optional(),
  calibration: Yup.number().optional().nullable(),
  minValue: Yup.number().optional().nullable(),
  maxValue: Yup.number()
    .optional()
    .nullable()
    .when("minValue", {
      is: (minValue: number) => minValue !== undefined,
      then: (schema) =>
        schema.min(
          Yup.ref("minValue"),
          "Max value must be greater than min value"
        ),
      otherwise: (schema) => schema,
    }),
});

export function AddParameterModal({
  isOpen,
  onClose,
  onSave,
  deviceId,
  editParameter,
  isEditMode = false,
}: AddParameterModalProps) {
  const { selectedDeviceId } = useDeviceStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // API mutations
  const { mutateAsync: createParameterMutation } = useCreateParameter();
  const { mutateAsync: updateParameterMutation } = useUpdateParameter();

  // Determine if we're in edit mode based on both props
  const isEditing = isEditMode || !!editParameter;

  const initialValues: ParameterFormData = {
    deviceId: editParameter?.deviceId || deviceId || selectedDeviceId || "",
    name: editParameter?.name || "",
    identifier: editParameter?.identifier || "",
    unit: editParameter?.unit || "",
    dataType: editParameter?.dataType || "INTEGER",
    icon: editParameter?.icon || "",
    minValue: editParameter?.minValue,
    maxValue: editParameter?.maxValue,
    calibration: editParameter?.calibration,
  };

  const handleSubmit = async (values: ParameterFormData, { resetForm }: any) => {
    setIsSubmitting(true);
    setError(null);

    // Validate deviceId before proceeding
    if (!values.deviceId || values.deviceId.trim() === "") {
      setError("No device selected. Please select a device first.");
      setIsSubmitting(false);
      return;
    }

    try {
      if (isEditing && editParameter) {
        // Handle update
        const updateData: ParameterUpdateDto = {
          identifier: values.identifier,
          name: values.name,
          unit: values.dataType === "STRING" ? "" : values.unit,
          dataType: values.dataType,
          icon: values.icon,
          minValue: values.minValue,
          maxValue: values.maxValue,
          calibration: values.calibration,
        };

        // Note: We need the parameter ID for update, which should be passed from parent
        // For now, we'll call onSave with the updated data
        await updateParameterMutation({
          id: editParameter.id || "", // This should be passed from parent
          data: updateData,
        },{
          onSuccess() {
            toast(
              'Parameter updated successfully',
              {
                type: "success",
                autoClose: 3000,
              },
            );
          },
          onError() {
            toast("Failed to update parameter. Please try again.", {
              type: "error",
              autoClose: 2000,
            });
          }
        });
      } else {
        // Handle create
        const createData: ParameterCreateDto = {
          identifier: values.identifier,
          name: values.name,
          unit: values.dataType === "STRING" ? "" : values.unit,
          dataType: values.dataType,
          deviceId: values.deviceId,
          icon: values.icon,
          calibration: values.calibration,
          minValue: values.minValue,
          maxValue: values.maxValue,
        };

        await createParameterMutation(createData, {
          onSuccess() {
            toast(
              'Parameter added successfully',
              {
                type: "success",
                autoClose: 3000,
              },
            );
          },
          onError() {
            toast("Failed to add parameter. Please try again.", {
              type: "error",
              autoClose: 2000,
            });
          }
        });
      }

      // Call the parent's onSave callback
      onSave(values);
      onClose();
      resetForm();
    } catch (error) {
      console.error("Error saving parameter:", error);
      setError("Failed to save parameter. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-6">
      <Card className="w-full max-w-4xl max-h-[95vh] overflow-auto bg-white dark:bg-slate-800 shadow-xl">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6">
          <CardTitle className="text-2xl font-semibold text-foreground">
            {isEditing ? "Edit Parameter" : "Add Parameter"}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 mb-4">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ values, setFieldValue }) => (
              <Form className="space-y-8">
                {/* Device ID - Hidden */}
                <Field name="deviceId" type="hidden" />

                {/* Grid Layout - 2 columns, 4 rows */}
                <div className="grid grid-cols-2 gap-x-12 gap-y-8">
                  {/* Column 1 */}
                  {/* Name */}
                  <div>
                    <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Name *
                    </label>
                    <Field
                      name="name"
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base"
                      placeholder="Enter parameter name"
                    />
                    <ErrorMessage name="name" component="p" className="text-red-500 text-sm mt-1" />
                  </div>

                  {/* Unit (not applicable to STRING parameters) */}
                  {values.dataType !== "STRING" && (
                    <div>
                      <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Unit *
                      </label>
                      <Field
                        name="unit"
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base"
                        placeholder="e.g., V, A, °C, %"
                      />
                      <ErrorMessage name="unit" component="p" className="text-red-500 text-sm mt-1" />
                    </div>
                  )}

                  {/* Identifier */}
                  <div>
                    <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Identifier *
                    </label>
                    <Field
                      name="identifier"
                      type="text"
                      disabled={isEditing}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base"
                      placeholder="Enter parameter identifier"
                    />
                    <ErrorMessage
                      name="identifier"
                      component="p"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  {/* Data Type */}
                  <div>
                    <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Data Type
                    </label>
                    <Field
                      name="dataType"
                      as="select"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base"
                    >
                      <option value="INTEGER">INTEGER</option>
                      <option value="FLOAT">FLOAT</option>
                      <option value="STRING">STRING</option>
                      <option value="BOOLEAN">BOOLEAN</option>
                    </Field>
                  </div>

                  {/* Icon */}
                  <div>
                    <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Icon (optional)
                    </label>
                    <IconSelector
                      value={values.icon}
                      onChange={(icon) => setFieldValue("icon", icon)}
                    />
                    <ErrorMessage name="icon" component="p" className="text-red-500 text-sm mt-1" />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Choose an icon or upload your own (used for UI display)
                    </p>
                  </div>

                  {/* Min Value (only for numeric types) */}
                  {(values.dataType === "INTEGER" || values.dataType === "FLOAT") && (
                    <div>
                      <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Min Value (optional)
                      </label>
                      <Field
                        name="minValue"
                        type="number"
                        step={values.dataType === "FLOAT" ? "0.01" : "1"}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base"
                        placeholder="Enter minimum value"
                      />
                      <ErrorMessage
                        name="minValue"
                        component="p"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  )}

                  {/* Max Value (only for numeric types) */}
                  {(values.dataType === "INTEGER" || values.dataType === "FLOAT") && (
                    <div>
                      <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Max Value (optional)
                      </label>
                      <Field
                        name="maxValue"
                        type="number"
                        step={values.dataType === "FLOAT" ? "0.01" : "1"}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base"
                        placeholder="Enter maximum value"
                      />
                      <ErrorMessage
                        name="maxValue"
                        component="p"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  )}

                  {/* Calibration (only for numeric types) */}
                  {(values.dataType === "INTEGER" || values.dataType === "FLOAT") && (
                    <div>
                      <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Calibration (optional)
                      </label>
                      <Field
                        name="calibration"
                        type="number"
                        step={0.01}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base"
                        placeholder="e.g., 0.991, 0.9815"
                      />
                      <ErrorMessage
                        name="calibration"
                        component="p"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  )}
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6">
                  <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex items-center text-white space-x-2"
                    disabled={isSubmitting}
                  >
                    <Plus className="h-4 w-4" />
                    <span>
                      {isSubmitting
                        ? "Saving..."
                        : isEditing
                        ? "Update Parameter"
                        : "Add Parameter"}
                    </span>
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </div>
  );
}
